import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrandContext, ProductType } from '../models/auth-state.model';

export interface BrandConfig {
  id: string;
  hostPrefixes: string[];
  themeClass: string;
  themeName: string;
  homeRoute: string;
  allowedProductTypes: readonly ProductType[];
  logoSrc: string;
  navLogoSrc?: string;
  iconSrc?: string;
  loginBgSrc: string;
  supportEmail: string;
  publicUrl?: string;
  isDefault?: boolean;
}

interface RuntimeBrandConfig {
  id?: string;
  hostPrefixes?: string[];
  themeClass?: string;
  themeName?: string;
  homeRoute?: string;
  allowedProductTypes?: ProductType[];
  logoSrc?: string;
  navLogoSrc?: string;
  iconSrc?: string;
  loginBgSrc?: string;
  supportEmail?: string;
  publicUrl?: string;
  isDefault?: boolean;
}

interface RuntimeAppConfig {
  brands?: RuntimeBrandConfig[];
}

@Injectable({ providedIn: 'root' })
export class BrandService {
  // Minimal fallback brands used only when runtime-config.js is absent.
  // The full brand list (compair, nash, brand3, brand4) lives in runtime-config.js
  // to avoid duplication. mergeWithRuntimeConfig() always prefers runtime config.
  private readonly defaultBrands: BrandConfig[] = [
    {
      id: 'ir',
      hostPrefixes: ['select-ir', 'ir'],
      themeClass: 'brand-ir',
      themeName: 'ir',
      homeRoute: '/home',
      allowedProductTypes: ['IR'],
      logoSrc: 'assets/img/ir_brand_white.svg',
      iconSrc: 'assets/img/ir_brand.svg',
      loginBgSrc: 'assets/img/login_background.jpg',
      supportEmail: 'airtreatment.techsupport@irco.com',
      publicUrl: 'https://select-ir.ir-digital.net',
      isDefault: true,
    },
    // local dev fallback — preserved by mergeWithRuntimeConfig regardless of runtime config
    {
      id: 'local',
      hostPrefixes: ['localhost', '127.0.0.1'],
      themeClass: 'brand-ir',
      themeName: 'ir',
      homeRoute: '/home',
      allowedProductTypes: ['IR'],
      logoSrc: 'assets/img/ir_brand_white.svg',
      iconSrc: 'assets/img/ir_brand.svg',
      loginBgSrc: 'assets/img/login_background.jpg',
      supportEmail: 'airtreatment.techsupport@irco.com',
      publicUrl: 'http://localhost:4200',
    },
  ];
  private readonly brands: BrandConfig[] = this.mergeWithRuntimeConfig(this.defaultBrands);

  private currentBrand: BrandConfig = this.getDefaultBrand();
  private currentHostname = '';

  private readonly ctx = new BehaviorSubject<BrandContext | null>(null);
  readonly brandContext$ = this.ctx.asObservable();
  private bootstrapPromise: Promise<void> | null = null;

  constructor(private readonly http: HttpClient) {
    this.detectBrand();
    this.applyTheme();
  }

  get brandId(): string | null {
    return this.ctx.getValue()?.brandId ?? null;
  }

  get categoryId(): string | null {
    return this.ctx.getValue()?.defaultCategoryId ?? null;
  }

  get context(): BrandContext | null {
    return this.ctx.getValue();
  }

  async bootstrap(): Promise<void> {
    if (this.bootstrapPromise !== null) {
      return this.bootstrapPromise;
    }

    this.bootstrapPromise = (async () => {
      try {
        const context = await firstValueFrom(this.http.get<BrandContext>(environment.auth.brandToken));
        this.ctx.next(context);
      } catch (err) {
        console.error('[BrandService] Failed to fetch brand context — X-Brand-Id and X-Category-Id headers will not be sent:', err);
        this.ctx.next(null);
      }
    })();

    return this.bootstrapPromise;
  }

  get current(): BrandConfig {
    return this.currentBrand;
  }

  detectBrand(hostname: string = globalThis.location?.hostname ?? ''): BrandConfig {
    const host = (hostname || '').toLowerCase();
    if (!host && typeof globalThis.location === 'undefined') {
      console.warn('[BrandService] globalThis.location is unavailable — brand detection defaults to the configured default brand.');
    }
    this.currentHostname = host;
    const match = this.brands.find((b) => b.hostPrefixes.some((p) => host.startsWith(p)));
    this.currentBrand = match ?? this.getDefaultBrand();
    return this.currentBrand;
  }

  applyTheme(): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }
    for (const b of this.brands) {
      if (b.themeClass?.trim()) {
        document.body.classList.remove(b.themeClass);
      }
    }
    const currentThemeClass = this.currentBrand.themeClass?.trim() || 'brand-ir';
    document.body.classList.add(currentThemeClass);
  }

  validateUserBrand(
    productType: ProductType | null | undefined,
    roles: string[] = [],
    requiredRoles: string[] = [],
  ): { ok: boolean; correctUrl?: string } {
    const pt = productType ?? 'NONE';
    const productTypeOk = this.currentBrand.allowedProductTypes.includes(pt);
    const roleOk = requiredRoles.length === 0 || requiredRoles.some((r) => roles.includes(r));

    if (productTypeOk && roleOk) {
      return { ok: true };
    }

    const correctBrand = this.brands.find((b) => b.allowedProductTypes.includes(pt));
    const correctUrl = correctBrand ? this.buildBrandRedirectUrl(correctBrand) : undefined;

    return { ok: false, correctUrl };
  }

  isKnownBrandUrl(url: string): boolean {
    const isLocalhost = /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/.test(url);
    if (isLocalhost) {
      return true;
    }
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return false;
    }
    const envSuffix = this.getCurrentEnvSuffix();
    if (envSuffix === null) {
      // No current-tier info — keep legacy publicUrl-exact match so we don't regress callers
      // that invoke this without a prior detectBrand().
      return this.brands.some((b) => {
        if (!b.publicUrl) {
          return false;
        }
        try {
          const brandUrl = new URL(b.publicUrl);
          return brandUrl.hostname === parsed.hostname && parsed.protocol === brandUrl.protocol;
        } catch {
          return false;
        }
      });
    }
    // Same-tier check: target hostname must equal `<brand-prefix><currentEnvSuffix>` for some
    // configured brand, with a matching protocol. Prevents test → prod (or vice-versa) redirects
    // even when runtime-config.js publicUrls point to a different tier.
    return this.brands.some((b) => {
      const prefix = b.hostPrefixes[0];
      if (!prefix || !b.publicUrl) {
        return false;
      }
      let brandProtocol: string;
      try {
        brandProtocol = new URL(b.publicUrl).protocol;
      } catch {
        brandProtocol = 'https:';
      }
      const expectedHostname = `${prefix}${envSuffix}`.toLowerCase();
      return parsed.hostname.toLowerCase() === expectedHostname && parsed.protocol === brandProtocol;
    });
  }

  /**
   * Returns the environment-tier suffix of the current hostname (everything after the matched
   * brand prefix). Returns null when the current hostname doesn't match any brand prefix or
   * when the suffix isn't a real domain segment (e.g., on localhost), so callers can refuse
   * to derive a cross-tier redirect URL.
   *
   * Examples (with brand prefix `select-ir`):
   *   `select-ir.ir-digital.net`       → `.ir-digital.net`
   *   `select-ir-test.ir-digital.net`  → `-test.ir-digital.net`
   *   `localhost`                      → null
   */
  private getCurrentEnvSuffix(): string | null {
    if (!this.currentHostname) {
      return null;
    }
    for (const b of this.brands) {
      for (const p of b.hostPrefixes) {
        if (this.currentHostname.startsWith(p)) {
          const suffix = this.currentHostname.slice(p.length);
          if (suffix.startsWith('-') || suffix.startsWith('.')) {
            return suffix;
          }
          return null;
        }
      }
    }
    return null;
  }

  /**
   * Builds a redirect URL for the target brand on the *same* environment tier as the current
   * host. Prevents cross-tier redirects (e.g., test → prod) by deriving the hostname from the
   * current host's suffix instead of using the static `publicUrl` from runtime config.
   * Returns undefined when the current tier can't be determined, so callers fall back to a
   * safe in-app navigation (e.g., /access-denied).
   */
  private buildBrandRedirectUrl(brand: BrandConfig): string | undefined {
    const envSuffix = this.getCurrentEnvSuffix();
    if (envSuffix === null) {
      return undefined;
    }
    const targetPrefix = brand.hostPrefixes[0];
    if (!targetPrefix || !brand.publicUrl) {
      return undefined;
    }
    let protocol: string;
    try {
      protocol = new URL(brand.publicUrl).protocol;
    } catch {
      protocol = 'https:';
    }
    return `${protocol}//${targetPrefix}${envSuffix}`;
  }

  private toBrandConfig(runtime: RuntimeBrandConfig, base?: BrandConfig): BrandConfig {
    const hostPrefixes = (runtime.hostPrefixes ?? base?.hostPrefixes ?? []).filter((p): p is string => !!p && p.trim().length > 0);
    const themeClass = (runtime.themeClass ?? base?.themeClass ?? 'brand-ir').trim() || 'brand-ir';
    const themeName = (runtime.themeName ?? base?.themeName ?? 'ir').trim() || 'ir';
    const homeRoute = (runtime.homeRoute ?? base?.homeRoute ?? '/home').trim() || '/home';

    const id = runtime.id ?? base?.id ?? 'unknown';
    const logoSrc = (runtime.logoSrc ?? base?.logoSrc ?? `assets/img/${id}-logo.svg`).trim() || `assets/img/${id}-logo.svg`;
    const loginBgSrc =
      (runtime.loginBgSrc ?? base?.loginBgSrc ?? 'assets/img/login_background.jpg').trim() || 'assets/img/login_background.jpg';
    const supportEmail =
      (runtime.supportEmail ?? base?.supportEmail ?? 'airtreatment.techsupport@irco.com').trim() || 'airtreatment.techsupport@irco.com';

    return {
      id,
      hostPrefixes,
      themeClass,
      themeName,
      homeRoute,
      allowedProductTypes: runtime.allowedProductTypes ?? base?.allowedProductTypes ?? ['IR'],
      logoSrc,
      navLogoSrc: (runtime.navLogoSrc ?? base?.navLogoSrc ?? '').trim() || undefined,
      iconSrc: runtime.iconSrc ?? base?.iconSrc,
      loginBgSrc,
      supportEmail,
      publicUrl: runtime.publicUrl ?? base?.publicUrl,
      isDefault: runtime.isDefault ?? base?.isDefault ?? false,
    };
  }

  private mergeWithRuntimeConfig(baseBrands: BrandConfig[]): BrandConfig[] {
    const runtimeBrands = this.getRuntimeConfig()?.brands;
    if (!runtimeBrands?.length) {
      return baseBrands;
    }

    const byId = new Map(baseBrands.map((b) => [b.id, b] as const));
    const merged = runtimeBrands.map((rb) => {
      const id = rb.id ?? '';
      const base = byId.get(id);
      return this.toBrandConfig(rb, base);
    });

    // Preserve any base brand the runtime config omitted — guards against a
    // mistyped or stripped-down runtime-config.js silently dropping a known brand
    // (e.g. IR or the local dev fallback) and breaking detectBrand/validateUserBrand.
    for (const base of baseBrands) {
      if (!merged.some((b) => b.id === base.id)) {
        merged.push(base);
      }
    }

    return merged.length > 0 ? merged : baseBrands;
  }

  private getDefaultBrand(): BrandConfig {
    const brand = this.brands.find((b) => b.isDefault) ?? this.brands[0];
    if (!brand) {
      throw new Error('[BrandService] No brands configured — at least one brand must be defined');
    }
    return brand;
  }

  private getRuntimeConfig(): RuntimeAppConfig | undefined {
    const config = (globalThis as typeof globalThis & { __RUNTIME_CONFIG__?: RuntimeAppConfig }).__RUNTIME_CONFIG__;
    if (config && (!config.brands || !Array.isArray(config.brands))) {
      console.warn('[BrandService] Invalid __RUNTIME_CONFIG__ structure: brands must be an array — ignoring runtime config');
      return undefined;
    }
    return config;
  }
}
